import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import _ from 'lodash';

import { Column, FilterItem, Project, SortColumn } from '../../../utils/models';
import { formatTime, paginate, transformPageRange } from '../../../utils/utilities';
import { useAppDispatch } from '../../../store/configureStore';
import { setFilters } from '../../../store/projectsQuery';

import AccordionItem from '../../custom-reports/custom-visual-inspection/accordion-item';
import Conditional from '../../common/conditional';
import ExcelExport from '../../common/excelExport';
import GeneralReportOverviewForm from '../../forms/generalReportOverviewForm';
import GeneralReportOverviewTableRow from './generalReportOverviewTable';
import Header from '../../blocks/header';
import InspectedItem from '../../blocks/inspected-item';
import ListGroup from '../../blocks/list-group';
import Loader from '../../themed/loader';
import Overview from '../../blocks/overview';
import Pagination from '../../themed/pagination';
import SyncTime from '../../blocks/sync-time';
import Select from '../../themed/select/select';
import TableHeader from '../../common/tableHeader';

import useGeneralReportOverviewItems from '../../../hooks/useGeneralReportOverviewItems';
import css from './style.module.scss';

interface SearchParams { 
    organisationId: '', 
    facilityId: '', 
    module: '', 
    inspectionTypeId: '',
    year: number;
    inspectableItems: string[],
}

const GeneralReportOverview: React.FC = () => {
    const [sortColumn, setSortColumn] = useState(initialSortColumn);
    const [sortOption, setSortOption] = useState('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [searchParams, setSearchParams] = useState<SearchParams>({ 
        organisationId: '', 
        facilityId: '', 
        module: '', 
        inspectionTypeId: '',
        year: new Date().getFullYear(),
        inspectableItems: [],
    });

    const dispatch = useAppDispatch();

    const { isLoading: isReportLoading, inspectedItems, overview, lastUpdated } = useGeneralReportOverviewItems({ searchParams });

    const handleParamsChange = (newSearchParams: SearchParams) => {
        const filters: FilterItem[] = [
            {
                filterName: 'organisationId',
                filterValue: newSearchParams.organisationId,
                filterMatchType: 'contains'
            },
            {
                filterName: 'facilityId',
                filterValue: newSearchParams.facilityId,
                filterMatchType: 'contains'
            },
            {
                filterName: 'module',
                filterValue: newSearchParams.module,
                filterMatchType: 'contains'
            },
            {
                filterName: 'inspectionTypeId',
                filterValue: newSearchParams.inspectionTypeId,
                filterMatchType: 'contains'
            },
            {
                filterName: 'year',
                filterValue: newSearchParams.year,
                filterMatchType: 'contains'
            },
        ];
       
        dispatch(setFilters(filters));
        setSearchParams(newSearchParams);
    };

    const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const pageSize: number = +e.target.value;
		const actualPageSize: number = pageSize === 0 ? 1 : pageSize;

		setPageSize(actualPageSize);
	};

    const handleSortOption = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newSortColumn = _.find(sortOptions, ['order', e.target.value]);
        if (newSortColumn) setSortColumn(_.pick(newSortColumn, ['path', 'order']) as SortColumn);

		setSortOption(e.target.value);
	};

    const dataToExport = useMemo(() => {
        return inspectedItems.map((item) => item.inspectedItems.map((inspected) => ({
            ID: inspected.id, 
            DOCUMENT_NUMBER: item.projectDetails.documentNumber,
            MODULE: item.projectDetails.module,
            INSPECTED_BY: item.projectDetails.supervisorName,
            INSPECTION_DATE: inspected.date ? formatTime(inspected.date) : 'In progress',
        }))).flat()
    }, [inspectedItems]);

    const paginatedData = useMemo(() => {
		let filtered = inspectedItems;

		const sorted = _.orderBy(filtered, [sortColumn.path], [sortColumn.order]);
		const paginated = paginate(sorted, currentPage, pageSize);

		return { totalCount: filtered.length, paginated };
	}, [currentPage, inspectedItems, pageSize, sortColumn]);

    return ( 
        <React.Fragment>
            <Header
				canViewCTA
				subtitle='General Report Overview'
				buttonIcon="kanban-fill"
				buttonTitle="Back to Projects"
			/>

			<div className={css['general-report-overview-body']}>
                <SyncTime lastSynced={lastUpdated} />

                <div className="divider my-4"></div>
				
                <div className={css['general-report-overview-section']}>
                    <GeneralReportOverviewForm onTrack={handleParamsChange}/>
                </div>
                
                <Conditional isVisible={!isReportLoading}>
                    <Conditional isVisible={searchParams.module.length > 0}>
                            <div className="divider my-4"></div>

                            <div className="container-fluid overflow-hidden my-4">
                                <div className="row">
                                    <div className="col">
                                        <Overview
                                            iconName="bi-check-circle-fill"
                                            title={`${overview.totalItemsToInspect.length}`}
                                            subtitle="Total Items in Module"
                                        />
                                    </div>
                                    
                                    <div className="col">
                                        <Overview
                                            iconName="bi-hourglass-split"
                                            title={`${overview.totalUnispectedItems.length}`}
                                            subtitle="Yet to be Inspected"
                                        />
                                    </div>
                                    
                                    <div className="col">
                                        <Overview
                                            iconName="bi-star-fill"
                                            title={`${overview.totalInspectedItems.length}`}
                                            subtitle="Total Inspected"
                                        />
                                    </div>

                                    <div className="col">
                                        <Overview
                                            iconName="bi-alarm-fill"
                                            title={`${overview.unpublishedInspectionItems.length}`}
                                            subtitle="Inspection in progress"
                                        />
                                    </div>
                                    
                                    <div className="col">
                                        <Overview
                                            iconName="bi-eyeglasses"
                                            title={`${overview.publishedInspectionItems.length}`}
                                            subtitle="Published for review"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="divider my-4"></div>

                            <AccordionItem isActive title='Click Here to Show Full Breakdown'>
                                <div className={css['general-report-overview-section']}>
                                    <div className="row gx-4">
                                        <div className="col">
                                            <div className={css['general-report-overview-section-title']}>Total Items in Module: {searchParams.module} ({overview.totalItemsToInspect.length})</div>
                                            <ListGroup  data={overview.totalItemsToInspect.map(lineNumber => ({id: lineNumber, name: lineNumber}))} />
                                        </div>
                                        <div className="col">
                                            <div className={css['general-report-overview-section-title']}>Inspected Items ({overview.totalInspectedItems.length})</div>
                                            <InspectedItem  data={overview.totalInspectedItems} />
                                        </div>
                                        <div className="col">
                                            <div className={css['general-report-overview-section-title']}>Not Inspected Items ({overview.totalUnispectedItems.length})</div>
                                            <ListGroup  data={overview.totalUnispectedItems.map((lineNumber) => ({id: lineNumber, name: lineNumber}))} />
                                        </div>
                                    </div>
                                </div>
                            </AccordionItem>

                            <div className="divider mt-4"></div>

                            <div className="d-flex justify-content-end align-items-center">
                                <ExcelExport data={dataToExport} label='Download as Excel' fileName='Summary' />
                            </div>

                            <div className="divider mt-2"></div>


                            <div className={css['general-report-overview-section']}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <Select
                                        name="page-size"
                                        placeholder={'Showing (' + pageSize + 'rows)'}
                                        options={transformPageRange(20)}
                                        value={pageSize}
                                        onChange={handlePageSizeChange}
                                    />

                                    <Select
                                        name="sort-option"
                                        placeholder="Sort By"
                                        options={sortOptions}
                                        value={sortOption}
                                        onChange={handleSortOption}
                                    />
                                </div>

                                <div className="table">
                                    <table>
                                        <TableHeader columns={columns} onSort={setSortColumn} sortColumn={sortColumn}/>

                                        <tbody>
                                            {paginatedData.paginated.map((item, index) => <GeneralReportOverviewTableRow item={item} columns={columns} key={index} />)}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="d-flex justify-content-end align-items-center mt-3">
                                    <Pagination
                                        itemsCount={paginatedData.totalCount}
                                        pageSize={pageSize}
                                        currentPage={currentPage}
                                        onPageChange={(page: number) => setCurrentPage(page)}
                                    />
                                </div>
                            </div>
                    </Conditional>
                </Conditional>

                <Loader isVisible={isReportLoading} />
			</div>
        </React.Fragment>
     );
};
 
const initialSortColumn: SortColumn = {
	path: 'projectDetails.updatedAt',
	order: 'desc',
};

const sortOptions = [
    {
        id: 'asc',
        name: 'Oldest to Newest',
        path: 'projectDetails.createdAt',
        order: 'asc',
    },
    {
        id: 'desc',
        name: 'Newest to Oldest',
        path: 'projectDetails.createdAt',
        order: 'desc',
    }
];

const columns: Column[] = [
	{
		path: 'documentNumber',
		label: 'Serial No.',
		content: (project: Project) => (
			<Link className={css['general-report-overview-document-number']} to={`/dashboard/projects/${project._id}`}>
				{project.documentNumber}
            </Link>
		),
	},
	{ path: 'module', label: 'Module' },
	{ path: 'supervisorName', label: 'Inspected By' },
	{ path: 'projectTitle', label: 'Project Title' },
];

export default GeneralReportOverview;